
import { cropTextSmart } from '@packages/toolbox';
import { http } from 'SharedDir/backend/axios/http';
import { QForm } from 'quasar';
import { PropType, computed, defineComponent, ref, watch } from 'vue';

import AttachmentIcon from 'SharedComponents/Attachments/AttachmentIcon.vue';
import Certificate from 'SharedComponents/Signature/Certificate.vue';
import Signer from 'SharedComponents/Signature/Signer.vue';
import SBanner from 'SharedComponents/base/Banner/SBanner.vue';
import SBtn from 'SharedComponents/ui/Button/SBtn.vue';
import SIcon from 'SharedComponents/ui/Icon/SIcon.vue';
import SModal from 'SharedComponents/ui/Modal/SModal.vue';
import { AttachmentsApi } from 'SharedDir/backend/api';
import { useNotifications } from 'SharedDir/common/composables/useNotifications';
import { usePermissions } from 'SharedDir/common/composables/usePermissions';
import useValidation from 'SharedDir/common/composables/useValidation';
import { useValue } from 'SharedDir/common/composables/useValue';
import { AvailableCertificate, SignerInfo, VendorInfo } from 'SharedDir/declarations/crypto-pro';
import { IEndpointData } from 'SharedDir/declarations/endpoint';
import { OneOf } from 'SharedDir/utils/components';
import {
  CryptoContainer,
  downloadSignature as downloadSignatureBase,
} from 'SharedDir/utils/crypto-pro';
import fileDownloader from 'SharedDir/utils/file-downloader';

export interface DocumentInfo {
  name: string;
  endpoint: IEndpointData;
  extension?: string | null;
  needSign: boolean;
  /** uuid вложения — для ленивого GET /signature (поля секций) */
  id?: string;
}

export default defineComponent({
  name: 'SignedDocument',
  components: { SBtn, SModal, SIcon, AttachmentIcon, Signer, Certificate, SBanner },
  props: {
    modelValue: { type: String as PropType<string | null>, default: null },
    document: { type: Object as PropType<DocumentInfo>, required: true },
    allowSigning: { type: Boolean, default: true },
    allowDownloadSignature: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    loading: { type: Boolean },
    signers: { type: Array as PropType<Omit<SignerInfo, 'thumbprint'>[]>, default: () => [] },
    menuPosition: { type: String, validator: OneOf(['left', 'right']), default: 'right' },
  },
  emits: ['update:signers', 'update:modelValue', 'signed'],
  setup(
    props: {
      modelValue: string | null;
      document: DocumentInfo;
      allowSigning?: boolean;
      allowDownloadSignature?: boolean;
      compact?: boolean;
      loading?: boolean;
      signers: Omit<SignerInfo, 'thumbprint'>[];
      menuPosition?: string;
    },
    { emit }
  ) {
    const innerSigners = ref(props.signers);
    const resolutionForm = ref<QForm>();
    const tab = ref('certificates');
    const signingPossible = ref(false);
    const showDialog = ref(false);
    const cryptoproLoading = ref(false);

    const certificates = ref([] as AvailableCertificate[]);

    const thumbprint = ref('' as null | string);

    const description = ref(null as null | string);

    const info = ref({
      pluginVersion: '',
      providerName: '',
      providerVersion: '',
    } as VendorInfo);

    const currentCertificate = ref(null as AvailableCertificate | null);

    const { innerValue, emitValue } = useValue<string | null>(props, emit);
    const { successNotification, failureNotification } = useNotifications();

    const canDownloadSignatureMenuItem = computed(
      () => Boolean(innerValue.value) && props.allowDownloadSignature
    );

    const canShowSignersMenuItem = computed(() => Boolean(innerValue.value));

    const hasMenuItems = computed(
      () =>
        Boolean(props.allowSigning) ||
        canDownloadSignatureMenuItem.value ||
        canShowSignersMenuItem.value
    );

    const dialogTitle = computed(() => {
      if (tab.value === 'certificates') {
        return 'Подписание документа';
      }
      return 'Информация о подписантах';
    });

    watch(
      () => innerSigners.value,
      (newValue) => {
        emit('update:signers', newValue);
      }
    );

    watch(
      () => props.signers,
      (newSigners) => {
        innerSigners.value = newSigners;
      }
    );

    async function withCSP<T>(fn: () => Promise<T>): Promise<T> {
      cryptoproLoading.value = true;
      try {
        return await fn();
      } finally {
        cryptoproLoading.value = false;
      }
    }

    function selectCertificate(certificate: AvailableCertificate) {
      thumbprint.value = certificate.thumbprint;
      currentCertificate.value = certificate;
    }

    async function init() {
      signingPossible.value = await withCSP(async () => {
        if (!CryptoContainer.loaded) {
          await CryptoContainer.Init();
        }
        certificates.value = await CryptoContainer.getAvailableCertificates();
        if (certificates.value.length === 1) {
          selectCertificate(certificates.value[0]);
        }
        info.value = await CryptoContainer.Check();
        return CryptoContainer.loaded;
      });
    }

    async function getFile() {
      return http({
        ...props.document.endpoint,
        responseType: 'blob',
      });
    }

    async function downloadFile() {
      const response = await getFile();
      fileDownloader.saveFileFromResponse(response);
    }

    async function refreshCertificates() {
      certificates.value = await withCSP(() => CryptoContainer.getAvailableCertificates(true));
    }

    async function ensureSignatureFromServer(): Promise<boolean> {
      if (innerValue.value || !props.document.id) {
        return Boolean(innerValue.value);
      }
      // fallback для экспорта заявления (подпись уже в modelValue)
      const content = await AttachmentsApi.getSignatureContent(props.document.id);
      if (!content) {
        return false;
      }
      innerValue.value = content;
      emitValue();
      return true;
    }

    async function ensureSignersFromSignature() {
      if (innerSigners.value.length > 0 || !innerValue.value) {
        return;
      }
      try {
        await withCSP(async () => {
          if (!CryptoContainer.loaded) {
            await CryptoContainer.Init();
          }
          const { data: dataToSign } = await getFile();
          const verification = await CryptoContainer.VerifySignature(
            dataToSign,
            innerValue.value as string
          );
          innerSigners.value = verification.signers.sort((firstSigner, secondSigner) =>
            firstSigner.signingTime > secondSigner.signingTime ? 1 : -1
          );
        });
      } catch {
        // Плагин недоступен или подпись не разобралась — список останется пустым
      }
    }

    async function openDialog(type: string) {
      if (type === 'signers') {
        const hasSignature = await ensureSignatureFromServer();
        if (!hasSignature) {
          failureNotification('У файла нет подписи');
          return;
        }
        await ensureSignersFromSignature();
      }
      tab.value = type;
      showDialog.value = true;
      if (type === 'certificates') {
        await init();
      }
    }

    function openSignDialog() {
      openDialog('certificates');
    }

    async function downloadSignature() {
      let signature = innerValue.value;
      if (!signature && props.document.id) {
        const loaded = await ensureSignatureFromServer();
        if (!loaded) {
          failureNotification('У файла нет подписи');
          return;
        }
        signature = innerValue.value;
      }
      if (!signature) {
        return;
      }
      const name = props.document.name || 'signature';
      const fileName = `${name}.sig`;
      downloadSignatureBase(signature, fileName);
    }

    async function sign() {
      if (!thumbprint.value || !(await resolutionForm.value?.validate())) {
        return;
      }
      await withCSP(async () => {
        const signatureBackup = innerValue.value;
        const thumbprintValue = thumbprint.value as string;
        const { data: dataToSign } = await getFile();
        let signature = innerValue.value;
        if (signature && (await CryptoContainer.VerifySignature(dataToSign, signature))) {
          // Если документ уже подписан и это валидная подпись то подписываем совместно
          // в противном случае мы просто затрем существующую подпись
          signature = await CryptoContainer.CoSignData(
            dataToSign,
            signature,
            thumbprintValue,
            description.value || ''
          );
        } else {
          signature = await CryptoContainer.SignData(
            dataToSign,
            thumbprintValue,
            description.value || ''
          );
        }
        if (!signature) {
          throw new Error('Не удалось сформировать подпись');
        }
        innerValue.value = signature;
        const { signers } = await CryptoContainer.VerifySignature(dataToSign, signature);
        const sortedSigners = signers.sort((a, b) => (a.signingTime > b.signingTime ? 1 : -1));
        innerSigners.value = sortedSigners;
        const currentSigner = sortedSigners.reverse().find((s) => s.thumbprint === thumbprintValue);
        if (!currentSigner) {
          throw new Error('Ошибка при чтении подписантов');
        }
        emitValue();
        // Для обеспечения безопасности, консистентности,
        // да и вообще рамок приличия на беке идет проверка переданной подписи.
        // А именно - что бы защитится от Отаки в ходе которой
        // нам подсовывают на сохранение валидную подпись,
        // но от другого подписанта и/или с другим временем подписи
        // для этого вместет с самой открепленной ЭЦП на бек уходит
        // и инфа о текущем подписанте
        const { serialNumber, signingTime, comment } = currentSigner;
        let resolve: () => void = () => undefined;
        let reject: () => void = () => undefined;
        const promise = new Promise<void>((res, rej) => {
          resolve = res;
          reject = rej;
        });
        emit('signed', {
          data: {
            signature,
            serialNumber,
            signingTime: signingTime.toISOString().slice(0, -5),
            comment,
          },
          done: resolve,
          fail: () => {
            innerValue.value = signatureBackup;
            emitValue();
            reject();
          },
        });
        await promise;
        description.value = '';
      });

      successNotification('Подписание документа прошло успешно!');
      showDialog.value = false;
    }

    return {
      cropTextSmart,
      innerSigners,
      resolutionForm,
      innerValue,
      emitValue,
      tab,
      signingPossible,
      showDialog,
      cryptoproLoading,
      certificates,
      thumbprint,
      description,
      info,
      currentCertificate,
      selectCertificate,
      downloadFile,
      refreshCertificates,
      openDialog,
      openSignDialog,
      downloadSignature,
      dialogTitle,
      sign,
      hasMenuItems,
      canDownloadSignatureMenuItem,
      canShowSignersMenuItem,
      ...usePermissions(),
      ...useValidation(),
    };
  },
});
